/**
 * "Confirm what you are asking for" — the moment the whole product turns on.
 *
 * A governed record does not change because someone typed. It changes because a chain of
 * people agreed. So between the keystroke and the application there is exactly one step, and
 * this is it.
 *
 * It is deliberately **not** a confirmation dialog in the usual sense. A dialog that asks
 * "Are you sure?" teaches people to click through it within a week. This one says *what
 * changed, from what, to what* — the same field-level diff the reviewers will see, before
 * anyone commits to asking. If the reader disagrees with what is on this card, they disagree
 * before three other people spend attention on it.
 *
 * Before and after are told apart by weight and a strike as well as by colour, because this
 * card gets printed into board packs in monochrome.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { ColumnInfo, RegisterInfo, RegisterRow } from '@/api/types'
import { Icon } from '@/components/Icon'
import type { Change } from '@/lib/diff'
import { formatValue } from '@/lib/format'
import { registerLabel } from '@/lib/registers'

export function ConfirmEdit({
  register,
  row,
  changes,
  packageId,
  onCancel,
  onRaised,
}: {
  register: RegisterInfo
  row: RegisterRow
  changes: Change[]
  packageId: string
  onCancel: () => void
  onRaised: (applicationNumber: string) => void
}) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const dialog = useRef<HTMLDivElement>(null)
  const confirm = useRef<HTMLButtonElement>(null)

  // Focus lands on the confirming action, and Escape is the way out. A modal that traps a
  // keyboard user is a modal that fails an accessibility review and, more to the point,
  // fails the person using it.
  useEffect(() => {
    confirm.current?.focus()
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [onCancel])

  const raise = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {}
      for (const change of changes) payload[change.field] = change.after

      const application = await api.post<{ id: string; number: string }>('/applications', {
        application_type: 'register_edit',
        title: defaultTitle(t, register, row),
        package_id: packageId,
        entity_table: register.slug,
        entity_id: row['id'],
        payload,
      })
      // Raised as a draft and submitted immediately. Two clicks to start a review the person
      // has already confirmed would be ceremony, and a draft nobody submits is worse than no
      // application — it looks like the work was done.
      await api.post(`/applications/${application.id}/submit`)
      return application
    },
    onSuccess: (application) => {
      void queryClient.invalidateQueries({ queryKey: ['inbox'] })
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
      onRaised(application.number)
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.friendly : String(caught))
    },
  })

  const kindOf = (field: string) =>
    register.columns.find((column) => column.name === field)?.kind ?? 'text'
  const labelOf = (field: string) =>
    register.columns.find((column) => column.name === field)?.label ?? field

  return (
    <>
      <div className="scrim" onClick={onCancel} data-testid="confirm-scrim" />
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        ref={dialog}
        data-testid="edit-confirm"
      >
        <header className="modal__head">
          <Icon name="warning" size={16} strokeWidth={1.7} />
          <h2 className="modal__title" id="confirm-title">
            {t('edit.confirmTitle')}
          </h2>
        </header>

        <div className="modal__body" data-scroll="1">
          {changes.map((change) => (
            <div className="change" key={change.field} data-field={change.field}>
              <p className="change__field">{labelOf(change.field)}</p>
              <div className="change__values">
                <span className="change__before" data-testid="change-before">
                  {render(change.before, kindOf(change.field), i18n.language, t, register, change)}
                </span>
                <Icon name="chevronEnd" size={14} strokeWidth={2} className="change__arrow" />
                <span className="change__after" data-testid="change-after">
                  {render(change.after, kindOf(change.field), i18n.language, t, register, change)}
                </span>
              </div>
            </div>
          ))}

          <p className="modal__note">{t('edit.confirmLead')}</p>

          {error ? (
            <p className="modal__error" role="alert" data-testid="edit-error">
              {error}
            </p>
          ) : null}
        </div>

        <footer className="modal__foot">
          <button
            type="button"
            className="button button--quiet"
            onClick={onCancel}
            disabled={raise.isPending}
            data-testid="edit-back"
          >
            {t('edit.keepEditing')}
          </button>
          <button
            type="button"
            className="button button--primary button--wide"
            onClick={() => raise.mutate()}
            disabled={raise.isPending}
            ref={confirm}
            data-testid="edit-raise"
          >
            {raise.isPending ? t('edit.raising') : t('edit.confirmSubmit')}
          </button>
        </footer>
      </div>
    </>
  )
}

/**
 * A value as it will read on the card.
 *
 * Reference columns are the reason this is not simply `formatValue`: the cell sends a lookup
 * id, and a diff that said `9f3c…` → `a17b…` would be unreviewable. The *before* label is on
 * the row already; the *after* one comes from the column's own option set.
 */
function render(
  value: unknown,
  kind: string,
  locale: string,
  t: TFunction,
  register: RegisterInfo,
  change: Change,
): string {
  if (kind === 'reference') {
    const column: ColumnInfo | undefined = register.columns.find((c) => c.name === change.field)
    const option = column?.options.find((candidate) => candidate.id === value)
    if (option) return option.label
    if (value === null || value === undefined || value === '') return '—'
  }
  return formatValue(value, kind, locale, t)
}

/**
 * The application's stored title.
 *
 * Deliberately not translated: it is read by every role in the approval chain whatever
 * language each of them is working in, and composing it in the raiser's language would make
 * the inbox a mix of two scripts and an application harder to find.
 */
function defaultTitle(t: TFunction, register: RegisterInfo, row: RegisterRow): string {
  const reference = row['register_number']
  const label = registerLabel(t, register.slug, register.label)
  return `Update ${label.toLowerCase()} ${reference ? String(reference) : ''}`.trim()
}
